import React, { useEffect, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import useTranslation from "../../hooks/useTranslation";
import { ChatMessage } from "../models/chatBotModels";
import { dt } from "../../themes/redesignTokens";
import accountContext from "../../../account/accountContext";

// ═══════════════════════════════════════════════════════════════
//  URL helpers
// ═══════════════════════════════════════════════════════════════

/** True for in-session optimistic blob: URLs (created via URL.createObjectURL) */
const isBlobUrl = (url: string): boolean => url.startsWith("blob:");

/**
 * True for bot-hosted image URLs that require authenticated proxy fetch
 * (MIDAS-BUG-001-P2: after reload, user-uploaded images come back as
 *  /api/v1/image/user-uploads/... paths served by the bot gateway).
 */
const isBotProxyPath = (url: string): boolean =>
  url.startsWith("/api/v1/image/") || url.includes("/api/v1/image/");

/**
 * Resolve a bot image URL to our server-side proxy endpoint.
 * Mirrors ChatSourceCard.resolveImageProxyUrl to keep behaviour consistent.
 */
const resolveImageProxyUrl = (imageUrl: string): string => {
  const baseUrl = (window as any).app?.config?.apiBaseUrl || "";
  const marker = "/api/v1/image/";
  const markerIdx = imageUrl.indexOf(marker);
  const stripped = markerIdx !== -1
    ? imageUrl.slice(markerIdx + marker.length)
    : imageUrl.replace(/^\/+/, "");
  return `${baseUrl}/v1/bot/image/${stripped}`;
};

// ═══════════════════════════════════════════════════════════════
//  ChatUserMessage
// ═══════════════════════════════════════════════════════════════

interface ChatUserMessageProps {
  message: ChatMessage;
  showTimestamp?: boolean;
  isPhone?: boolean;
  /** Called with the RENDERABLE image URL (blob or direct) + filename when the user clicks the image */
  onImageClick?: (imageUrl: string, imageName: string) => void;
}

const ChatUserMessage: React.FC<ChatUserMessageProps> = ({
  message,
  showTimestamp = false,
  isPhone = false,
  onImageClick,
}) => {
  const { translate, i18n } = useTranslation("chatBot");

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    // Active UI language (not the browser's), 24 h like every other platform time (HH:mm).
    return d.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  };

  // Extract optional width/height from the user_image artifact to reserve space
  // and avoid layout shift while the proxy fetch is in flight.
  const userImageArtifact = message.artifacts?.find((a) => a.type === "user_image");
  const imgWidth = (userImageArtifact?.data as any)?.width as number | undefined;
  const imgHeight = (userImageArtifact?.data as any)?.height as number | undefined;

  // Legacy safety-net: treat "[image]" literal as empty text (pre-P2 rows).
  const rawText = message.text ?? "";
  const displayText = rawText === "[image]" ? "" : rawText;
  const hasText = displayText.trim().length > 0;

  const sourceUrl = message.imageUrl || "";
  const hasImageUrl = sourceUrl.length > 0;

  // Resolved URL we actually hand to <img src>. For blob URLs we use as-is;
  // for bot-proxy paths we fetch via authenticated proxy → blob URL.
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(
    hasImageUrl && isBlobUrl(sourceUrl) ? sourceUrl : null
  );
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasImageUrl) {
      setResolvedUrl(null);
      return;
    }

    // Blob URL: use directly (in-session optimistic)
    if (isBlobUrl(sourceUrl)) {
      setResolvedUrl(sourceUrl);
      return;
    }

    // Bot-hosted path: fetch via authenticated proxy → blob URL
    if (!isBotProxyPath(sourceUrl)) {
      // Unknown URL shape — render directly as last resort
      setResolvedUrl(sourceUrl);
      return;
    }

    let cancelled = false;
    const proxyUrl = resolveImageProxyUrl(sourceUrl);
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    fetch(proxyUrl, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setResolvedUrl(url);
      })
      .catch(() => {
        // Silently collapse — the image bubble will render nothing
        // when resolvedUrl stays null (MIDAS-BUG-003).
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUrl]);

  const hasImage = hasImageUrl;

  const handleImageClick = () => {
    if (resolvedUrl && onImageClick) {
      onImageClick(resolvedUrl, message.imageName || "image");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        animation: "midasUserMsg 0.2s cubic-bezier(0.4, 0, 0.2, 1) both",
      }}
    >
      <Box
        sx={{
          maxWidth: isPhone ? "90%" : "85%",
          backgroundColor: dt.blue,
          borderRadius: "14px 4px 14px 14px",
          padding: hasImage && !hasText ? "6px" : "12px 16px",
          fontFamily: dt.font,
          fontSize: "14px",
          fontWeight: 400,
          color: "#FFFFFF",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          borderLeft: message.sendFailed ? "3px solid #E74C3C" : "none",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: hasImage && hasText ? "8px" : 0,
        }}
      >
        {/* Image thumbnail */}
        {hasImage && (
          <Box
            onClick={resolvedUrl ? handleImageClick : undefined}
            sx={{
              width: "100%",
              maxWidth: "240px",
              borderRadius: hasText ? "8px" : "10px",
              overflow: "hidden",
              cursor: onImageClick && resolvedUrl ? "pointer" : "default",
              transition: "opacity 0.15s ease",
              "&:hover": onImageClick && resolvedUrl ? { opacity: 0.85 } : {},
              // Pre-reserve space when dims are known (reduces CLS on reload)
              aspectRatio:
                !resolvedUrl && imgWidth && imgHeight
                  ? `${imgWidth} / ${imgHeight}`
                  : undefined,
              backgroundColor: !resolvedUrl ? "rgba(255,255,255,0.12)" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: !resolvedUrl ? "80px" : undefined,
            }}
          >
            {resolvedUrl ? (
              <img
                src={resolvedUrl}
                alt={message.imageName || translate("midas.userImage.uploadLabel")}
                loading="lazy"
                decoding="async"
                width={imgWidth}
                height={imgHeight}
                // MIDAS-BUG-003: silently collapse on load failure (stale blob
                // URLs, 404s, etc). Setting resolvedUrl to null unmounts the
                // <img> and the bubble renders empty — no misleading error text.
                onError={() => setResolvedUrl(null)}
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  objectFit: "cover",
                  display: "block",
                  borderRadius: hasText ? "8px" : "10px",
                }}
              />
            ) : null}
          </Box>
        )}

        {/* Text content */}
        {hasText && <span>{displayText}</span>}

        {message.sendFailed && (
          <Tooltip title={translate("sendError")} arrow>
            <ErrorOutline
              sx={{
                fontSize: "14px",
                color: "#FFCDD2",
                position: "absolute",
                bottom: "4px",
                right: "4px",
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Timestamp */}
      {showTimestamp && (
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "10px",
            fontWeight: 400,
            color: dt.textMuted,
            mt: "4px",
            mr: "4px",
          }}
        >
          {formatTime(message.timestamp)}
        </Box>
      )}
    </Box>
  );
};

export default ChatUserMessage;
